/**
 * <mb-cart-drawer>: carrinho lateral (portado de common/headerCart.js + components/store.js).
 * - Abre por [data-open-cart] (cabeçalho) e pelo evento mb:cart:updated com { open: true };
 *   fecha por [data-close-cart], pelo fundo e por Esc.
 * - Quantidade (+/−) e remoção usam /cart/change.js pela linha, com o parâmetro `sections` da API do carrinho:
 *   a própria resposta traz o HTML novo da seção, então não é preciso um segundo pedido.
 * - O contador do cabeçalho ([data-cart-count]) é atualizado a cada alteração, também quando o tema está
 *   configurado como carrinho em página (aí não existe drawer na tela).
 */
const { trapFocus, emit } = window.MB;


/** Atualiza o contador do cabeçalho (usado só dentro deste arquivo). */
function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
  });
}

/** Mensagem de erro do carrinho (422 da API), com as strings do tema. */
function errorMessage(data, fallback) {
  if (data && (data.description || data.message)) return data.description || data.message;
  return fallback || '';
}

class MbCartDrawer extends HTMLElement {
  /** Nome do arquivo da seção, usado pela Section Rendering API. */
  get sectionId() {
    return 'cart-drawer';
  }

  connectedCallback() {
    this.onCartUpdated = this.onCartUpdated.bind(this);

    document.querySelectorAll('[data-open-cart]').forEach((button) => {
      if (button.dataset.cartReady) return;
      button.dataset.cartReady = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(button);
      });
    });

    this.addEventListener('click', (event) => {
      const close = event.target.closest('[data-close-cart]');
      if (close) {
        event.preventDefault();
        this.close();
        return;
      }

      const quantity = event.target.closest('[data-quantity-change]');
      if (quantity) {
        event.preventDefault();
        this.changeQuantity(quantity);
        return;
      }

      const remove = event.target.closest('[data-item-remove]');
      if (remove) {
        event.preventDefault();
        this.removeItem(remove);
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    });

    document.addEventListener('mb:cart:updated', this.onCartUpdated);
  }

  disconnectedCallback() {
    document.removeEventListener('mb:cart:updated', this.onCartUpdated);
  }

  get isOpen() {
    return this.classList.contains('-visible');
  }

  onCartUpdated(event) {
    const detail = (event && event.detail) || {};
    if (detail.fromDrawer) return;
    this.refresh().then(() => {
      if (detail.open) this.open();
    });
  }

  open(trigger) {
    this.trigger = trigger || this.trigger;
    this.classList.add('-visible');
    document.documentElement.classList.add('cart-open');
    this.releaseFocus = trapFocus(this, this.querySelector('[data-close-cart]'));
    emit('mb:cart:open');
  }

  close() {
    this.classList.remove('-visible');
    document.documentElement.classList.remove('cart-open');
    if (this.releaseFocus) this.releaseFocus();
    if (this.trigger) this.trigger.focus();
    emit('mb:cart:close');
  }

  /** Busca o HTML atual da seção e troca o conteúdo do painel. */
  async refresh(html) {
    if (typeof html !== 'string') {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url.toString());
      if (!response.ok) return null;
      html = await response.text();
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(this.tagName.toLowerCase());
    if (!next) return null;
    this.innerHTML = next.innerHTML;
    // O contador do cabeçalho vem do próprio HTML re-renderizado (a adição por Ajax não devolve item_count).
    if (next.dataset.itemCount !== undefined) {
      this.dataset.itemCount = next.dataset.itemCount;
      updateCartCount(next.dataset.itemCount);
    }
    this.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    return this;
  }

  setLoading(state) {
    this.classList.toggle('-loading', Boolean(state));
  }

  showError(message) {
    const box = this.querySelector('[data-cart-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  }

  async changeQuantity(button) {
    const item = button.closest('[data-item-cart]');
    if (!item || item.classList.contains('updating')) return;
    const delta = Number(button.dataset.quantityChange || 0);
    const current = Number(item.dataset.itemQuantity || 1);
    const max = Number(item.dataset.itemMax || 0);
    const next = current + delta;
    if (next < 1) return;
    if (max > 0 && next > max) {
      const strings = (window.theme && window.theme.strings) || {};
      this.showError(strings.maxQuantity);
      return;
    }
    item.classList.add('updating');
    await this.change(item.dataset.line, next);
    item.classList.remove('updating');
  }

  async removeItem(button) {
    const item = button.closest('[data-item-cart]');
    if (!item || item.classList.contains('removing')) return;
    item.classList.add('removing');
    await this.change(item.dataset.line, 0);
  }

  /** POST /cart/change.js com o HTML da seção na resposta. */
  async change(line, quantity) {
    this.setLoading(true);
    this.showError('');
    try {
      const response = await fetch(`${window.theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity, sections: this.sectionId }),
      });
      const data = await response.json();
      if (!response.ok) throw data;
      updateCartCount(data.item_count);
      const html = data.sections && data.sections[this.sectionId];
      await this.refresh(typeof html === 'string' ? html : undefined);
      emit('mb:cart:updated', { cart: data, fromDrawer: true });
    } catch (data) {
      const strings = (window.theme && window.theme.strings) || {};
      await this.refresh();
      this.showError(errorMessage(data, strings.addError));
    }
    this.setLoading(false);
  }
}

/**
 * <mb-cart-page>: a página /cart usa a mesma mecânica de quantidade, remoção e re-render,
 * só que sem painel para abrir e fechar.
 */
class MbCartPage extends MbCartDrawer {
  get sectionId() {
    return 'main-cart';
  }

  get isOpen() {
    return false;
  }

  open() {}

  close() {}
}

if (!customElements.get('mb-cart-drawer')) customElements.define('mb-cart-drawer', MbCartDrawer);
if (!customElements.get('mb-cart-page')) customElements.define('mb-cart-page', MbCartPage);

/**
 * Sem drawer na página (tipo de carrinho = página, ou template sem o grupo de overlays),
 * o contador do cabeçalho ainda precisa acompanhar as adições feitas por Ajax.
 */
document.addEventListener('mb:cart:updated', async (event) => {
  if (document.querySelector('mb-cart-drawer, mb-cart-page')) return;
  const detail = (event && event.detail) || {};
  if (detail.cart && typeof detail.cart.item_count === 'number') {
    updateCartCount(detail.cart.item_count);
    return;
  }
  try {
    const response = await fetch(`${window.theme.routes.cart}.js`);
    if (!response.ok) return;
    const cart = await response.json();
    updateCartCount(cart.item_count);
  } catch (error) {
    /* silencioso: o contador volta ao valor certo na próxima navegação */
  }
});
